import { EquipSlot } from '@common/components/components';
import { ALL_EQUIP_SLOTS } from '@common/data/BodyTypeSlots';

import { ClientWorldService } from './ClientWorldService';
import { InventoryUIService } from './InventoryUIService';

/**
 * Service for handling client-side inventory and equipment logic that requires
 * entity ID translation or deferred resolution.
 *
 * This decouples specific component handling from the generic ClientWorldService.
 */
export class ClientInventoryService {
  // Map of ServerID -> Array of pending updates { parentClientEntityId, slotIndex }
  private pendingInventoryItems: Map<
    number,
    Array<{ parentClientEntityId: number; slotIndex: number }>
  > = new Map();

  // Map of ServerID -> Array of pending updates { parentClientEntityId, slotName }
  private pendingEquipmentItems: Map<
    number,
    Array<{ parentClientEntityId: number; slotName: string }>
  > = new Map();

  private inventoryUI: InventoryUIService | null = null;

  constructor(private clientWorld: ClientWorldService) {}

  /**
   * Set the UI service to trigger refreshes on resolution.
   */
  public setInventoryUIService(service: InventoryUIService): void {
    this.inventoryUI = service;
  }

  /**
   * Called when the world is reset (e.g. on sync).
   */
  public onWorldReset(): void {
    this.pendingInventoryItems.clear();
    this.pendingEquipmentItems.clear();
  }

  /**
   * Called when a new networked entity is created.
   * Checks if this entity resolves any pending inventory or equipment slots.
   */
  public onEntityCreated(serverEntityId: number, clientEntityId: number): void {
    // Check pending inventory items
    this.resolvePendingInventoryItems(serverEntityId, clientEntityId);
    // Check pending equipment items
    this.resolvePendingEquipmentItems(serverEntityId, clientEntityId);
  }

  /**
   * Translate server entity IDs in Inventory component data to client entity IDs.
   */
  public translateInventoryData(data: unknown, clientEntityId: number): unknown {
    if (!this.isInventoryData(data)) return data;

    const newSlots = data.slots.map((slotId, index) => {
      if (slotId === null) return null;

      const clientSlotId = this.clientWorld.getClientEntityId(slotId);

      if (clientSlotId === undefined) {
        // Defer resolution - item hasn't arrived yet
        this.addPendingInventoryItem(slotId, clientEntityId, index);
        return null;
      }
      return clientSlotId;
    });

    return { ...data, slots: newSlots };
  }

  /**
   * Translate server entity IDs in Equipment component data to client entity IDs.
   */
  public translateEquipmentData(data: unknown, clientEntityId: number): unknown {
    if (!this.isEquipmentData(data)) return data;

    const newSlots: Partial<Record<string, number | null>> = {};
    for (const [slotName, serverId] of Object.entries(data.slots)) {
      if (serverId === null || serverId === undefined) {
        newSlots[slotName] = null;
      } else {
        const clientSlotId = this.clientWorld.getClientEntityId(serverId);

        if (clientSlotId === undefined) {
          // Item hasn't arrived yet - defer resolution
          this.addPendingEquipmentItem(serverId, clientEntityId, slotName);
          newSlots[slotName] = null;
        } else {
          newSlots[slotName] = clientSlotId;
        }
      }
    }

    return { ...data, slots: newSlots };
  }

  private isInventoryData(data: unknown): data is { slots: (number | null)[] } {
    return (
      data !== null &&
      typeof data === 'object' &&
      'slots' in data &&
      Array.isArray((data as { slots: unknown }).slots)
    );
  }

  private isEquipmentData(
    data: unknown,
  ): data is { slots: Partial<Record<string, number | null>> } {
    return (
      data !== null &&
      typeof data === 'object' &&
      'slots' in data &&
      typeof (data as { slots: unknown }).slots === 'object'
    );
  }

  private addPendingInventoryItem(
    serverItemId: number,
    parentClientEntityId: number,
    slotIndex: number,
  ): void {
    const pending = this.pendingInventoryItems.get(serverItemId) || [];
    pending.push({ parentClientEntityId, slotIndex });
    this.pendingInventoryItems.set(serverItemId, pending);
  }

  private resolvePendingInventoryItems(serverItemId: number, clientItemId: number): void {
    const pending = this.pendingInventoryItems.get(serverItemId);
    if (!pending) return;

    const world = this.clientWorld.getWorld();

    let hasChanged = false;
    for (const { parentClientEntityId, slotIndex } of pending) {
      const inventory = world.getComponent(parentClientEntityId, 'Inventory');
      if (
        inventory &&
        slotIndex >= 0 &&
        slotIndex < inventory.slots.length &&
        inventory.slots[slotIndex] === null
      ) {
        // Update the slot with the now-available client entity ID
        inventory.slots[slotIndex] = clientItemId;
        hasChanged = true;
      }
    }

    this.pendingInventoryItems.delete(serverItemId);

    // Notify UI that a slot has been resolved, but only if something actually changed
    if (hasChanged) {
      this.inventoryUI?.notifyRefresh();
    }
  }

  private addPendingEquipmentItem(
    serverItemId: number,
    parentClientEntityId: number,
    slotName: string,
  ): void {
    const pending = this.pendingEquipmentItems.get(serverItemId) || [];
    pending.push({ parentClientEntityId, slotName });
    this.pendingEquipmentItems.set(serverItemId, pending);
  }

  private resolvePendingEquipmentItems(serverItemId: number, clientItemId: number): void {
    const pending = this.pendingEquipmentItems.get(serverItemId);
    if (!pending) return;

    const world = this.clientWorld.getWorld();

    let hasChanged = false;
    for (const { parentClientEntityId, slotName } of pending) {
      const equipment = world.getComponent(parentClientEntityId, 'Equipment');
      if (equipment) {
        // Validate slotName is a valid EquipSlot
        if (!ALL_EQUIP_SLOTS.includes(slotName as EquipSlot)) {
          console.warn(`[ClientInventoryService] Unknown equipment slot name: ${slotName}`);
          continue;
        }

        const slotKey = slotName as EquipSlot;
        // Verify slot is actually empty before overwriting
        if (equipment.slots[slotKey] === null) {
          equipment.slots[slotKey] = clientItemId;
          hasChanged = true;
        } else {
          console.warn(
            `[ClientInventoryService] Pending equipment item could not be resolved: slot '${slotKey}' on entity ${parentClientEntityId} is already filled (existing=${equipment.slots[slotKey]}, pendingClientItemId=${clientItemId}, serverItemId=${serverItemId}).`,
          );
        }
      }
    }

    this.pendingEquipmentItems.delete(serverItemId);

    // Notify UI that an equipment slot has been resolved, but only if something actually changed
    if (hasChanged) {
      this.inventoryUI?.notifyRefresh();
    }
  }
}
